import React, { useState, useEffect } from "react"
import {
  BaseLabelJSON,
  RecipeNutritionalInfo,
} from "../../constants/exportsCountries"
import { Popover } from "@mui/material"
import { CiViewList } from "react-icons/ci"
import {
  DOMESTIC_KEYS_TO_RECIPES_NI_KEYS_MAPPING,
  DOMESTIC_NUTRITION_KEYS,
  NI_VALUES_OPTIONS,
} from "../../Utils/constants"
import { RECIPE_NI_KEYS } from "../../pages/NutritionListV2/helper"

const NUTRIENT_LABELS: Record<string, string> = {
  [RECIPE_NI_KEYS.ENERGY]: "Energy (kcal)",
  [RECIPE_NI_KEYS.CARBOHYDRATES]: "Carbohydrates (g)",
  [RECIPE_NI_KEYS.TOTAL_SUGAR]: "Free Sugars (g)",
  [RECIPE_NI_KEYS.ADDED_SUGAR]: "Added Sugars (g)",
  [RECIPE_NI_KEYS.DIETARY_FIBRE]: "Dietary Fibre (g)",
  [RECIPE_NI_KEYS.PROTEIN]: "Protein (g)",
  [RECIPE_NI_KEYS.TOTAL_FAT]: "Total Fat (g)",
  [RECIPE_NI_KEYS.TOTAL_SFA]: "Saturated Fat (g)",
  [RECIPE_NI_KEYS.TRANS_FAT]: "Trans Fat (g)",
  [RECIPE_NI_KEYS.CHOLESTEROL]: "Cholesterol (mg)",
  [RECIPE_NI_KEYS.SODIUM]: "Sodium (mg)",
  [RECIPE_NI_KEYS.POTASSIUM]: "Potassium (mg)",
  [RECIPE_NI_KEYS.CALCIUM]: "Calcium (mg)",
  [RECIPE_NI_KEYS.IRON]: "Iron (mg)",
  [RECIPE_NI_KEYS.ZINC]: "Zinc (mg)",
  [RECIPE_NI_KEYS.VIT_C_ASCORBIC_ACID]: "Vitamin C (mg)",
  [RECIPE_NI_KEYS.VIT_A]: "Vitamin A (mcg)",
  [RECIPE_NI_KEYS.VIT_D]: "Vitamin D (mcg)",
  [RECIPE_NI_KEYS.TOTAL_MUFA]: "Mufa (g)",
  [RECIPE_NI_KEYS.TOTAL_PUFA]: "Pufa (g)",
}

const DOMESTIC_NUTRIENT_LABELS: Record<string, string> = {
  [DOMESTIC_NUTRITION_KEYS.DIETARY_FIBRE]: "Dietary Fibre (g)",
  [DOMESTIC_NUTRITION_KEYS.ORYZANOL]: "Oryzanol (mg)",
  [DOMESTIC_NUTRITION_KEYS.IRON]: "Iron (mg)",
  [DOMESTIC_NUTRITION_KEYS.CALCIUM]: "Calcium (mg)",
  [DOMESTIC_NUTRITION_KEYS.FOLIC_ACID]: "Folic Acid (mcg)",
  [DOMESTIC_NUTRITION_KEYS.IODINE]: "Iodine (mg)",
  [DOMESTIC_NUTRITION_KEYS.VITAMIN_B6]: "Vitamin B6 (mg)",
  [DOMESTIC_NUTRITION_KEYS.VITAMIN_B12]: "Vitamin B12 (mcg)",
  [DOMESTIC_NUTRITION_KEYS.VITAMIN_C]: "Vitamin C (mg)",
  [DOMESTIC_NUTRITION_KEYS.ZINC]: "Zinc (mg)",
  [DOMESTIC_NUTRITION_KEYS.MAGNESIUM]: "Magnesium (mg)",
  [DOMESTIC_NUTRITION_KEYS.VITAMIN_D]: "Vitamin D (mcg)",
  [DOMESTIC_NUTRITION_KEYS.VITAMIN_K1]: "Vitamin K1 (mcg)",
  [DOMESTIC_NUTRITION_KEYS.VITAMIN_B1]: "Vitamin B1 (mg)",
  [DOMESTIC_NUTRITION_KEYS.VITAMIN_B2]: "Vitamin B2 (mg)",
  [DOMESTIC_NUTRITION_KEYS.BIOTIN]: "Biotin (mcg)",
  [DOMESTIC_NUTRITION_KEYS.VITAMIN_B3]: "Vitamin B3 (mg)",
  [DOMESTIC_NUTRITION_KEYS.PANTOTHENIC_ACID]: "Pantothenic Acid (mg)",
  [DOMESTIC_NUTRITION_KEYS.VITAMIN_E]: "Vitamin E (mg)",
  [DOMESTIC_NUTRITION_KEYS.VITAMIN_A]: "Vitamin A (mcg)",
  [DOMESTIC_NUTRITION_KEYS.SELENIUM]: "Selenium (mcg)",
  [DOMESTIC_NUTRITION_KEYS.COPPER]: "Copper (mg)",
  [DOMESTIC_NUTRITION_KEYS.POTASSIUM]: "Potassium (mg)",
  [DOMESTIC_NUTRITION_KEYS.CHLORIDE]: "Chloride (mg)",
  [DOMESTIC_NUTRITION_KEYS.MANGANESE]: "Manganese (mg)",
  [DOMESTIC_NUTRITION_KEYS.PHOSPHORUS]: "Phosphorus (mg)",
  [DOMESTIC_NUTRITION_KEYS.MUFA]: "Mufa (g)",
  [DOMESTIC_NUTRITION_KEYS.PUFA]: "Pufa (g)",
  [DOMESTIC_NUTRITION_KEYS.OMEGA_3_FATTY_ACIDS]: "Omega 3 fatty acids (g)",
  [DOMESTIC_NUTRITION_KEYS.OMEGA_6_FATTY_ACIDS]: "Omega 6 fatty acids (g)",
}

const NiInfoPopOver = ({ values }: { values: BaseLabelJSON }) => {
  const [niInfoAnchor, setNiInfoAnchor] = useState<null | HTMLElement>(null)
  const [nutritionTable, setNutritionTable] = useState<
    { key: string; name: string; quantity: number | string }[]
  >([])
  const is_open = Boolean(niInfoAnchor)
  const ni_info_aria = is_open ? "food-items-popover" : undefined
  const ni_info_type = values.ni_info_type

  useEffect(() => {
    if (ni_info_type === NI_VALUES_OPTIONS.MY_RECIPES) {
      const recipe_ni_info: RecipeNutritionalInfo | undefined =
        values.extraData?.selected_recipe?.ni_info
      const ni_multiplier = 100 / values.serving_size
      if (recipe_ni_info) {
        const nutrition_info = Object.entries(recipe_ni_info)
          .filter(([, value]) => typeof value === "number" && value > 0)
          .map(([key, value]) => ({
            key,
            name: NUTRIENT_LABELS[key] || key,
            quantity: parseFloat((value * ni_multiplier).toFixed(1)),
          }))
        setNutritionTable(nutrition_info)
      }
    } else if (
      ni_info_type === NI_VALUES_OPTIONS.OWN_VALUES ||
      ni_info_type === NI_VALUES_OPTIONS.LAB_REPORT
    ) {
      const other_nutrient_information = values.non_mandatory_nutrient
      if (other_nutrient_information) {
        const nutrition_info = other_nutrient_information
          .filter((nutrient) => parseFloat(nutrient.quantity) > 0)
          .map((nutrient) => ({
            key: nutrient.name,
            name:
              NUTRIENT_LABELS[
                DOMESTIC_KEYS_TO_RECIPES_NI_KEYS_MAPPING[nutrient.name]
              ] || DOMESTIC_NUTRIENT_LABELS[nutrient.name],
            quantity: parseFloat(nutrient.quantity).toFixed(1),
          }))
        setNutritionTable(nutrition_info)
      }
    }
  }, [values])

  const handleOpenSeeFoodItems = (event: any) => {
    setNiInfoAnchor(event.currentTarget)
  }

  const handleCloseSeeFoodItems = () => {
    setNiInfoAnchor(null)
  }

  return (
    <div>
      <div className="text-xs font-medium text-[#555] mt-2 flex items-center gap-3 truncate hover:cursor-pointer">
        Refer Nutrient Values
        <CiViewList onClick={handleOpenSeeFoodItems} size={20} color="#000" />
      </div>
      <Popover
        id={ni_info_aria}
        open={is_open}
        anchorEl={niInfoAnchor}
        disableScrollLock={true}
        onClose={handleCloseSeeFoodItems}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        slotProps={{
          paper: {
            style: {
              border: "2px solid #ccc",
              borderRadius: "8px",
              padding: "10px",
              paddingTop: 0,
              maxHeight: "400px",
            },
          },
        }}
      >
        <div>
          <div className="py-2">
            {" "}
            <span className="text-black font-bold">Source : </span>
            {ni_info_type === NI_VALUES_OPTIONS.MY_RECIPES
              ? `${values?.extraData?.selected_recipe?.name} (Recipe)`
              : ` ${values?.domestic_title} (Domestic Label)`}{" "}
          </div>
          <table className="min-w-full border-collapse border border-gray-300">
            <thead>
              <tr>
                <th className="border border-gray-300 px-4 py-2 text-left">
                  Nutrient
                </th>
                <th className="border border-gray-300 px-4 py-2 text-left">
                  Quantity
                </th>
              </tr>
            </thead>
            <tbody>
              {nutritionTable.map((item) => (
                <tr key={item.key}>
                  <td className="border text-sm border-gray-300 px-2 py-2">
                    {item.name}
                  </td>
                  <td className="border text-sm border-gray-300 px-2 py-2">
                    {item.quantity}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Popover>
    </div>
  )
}

export default NiInfoPopOver
